﻿package com.mgacreative.hotelru.feature.room.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.domain.repository.ReservationRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

import com.mgacreative.hotelru.domain.repository.RoomRepository
import com.mgacreative.hotelru.domain.model.Room
import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil

import com.mgacreative.hotelru.domain.repository.GuestRepository
import com.mgacreative.hotelru.domain.repository.AgencyRepository
import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.domain.model.Agency
import com.mgacreative.hotelru.domain.model.Guest
import com.mgacreative.hotelru.domain.model.Reservation

data class RoomStatusDisplay(
    val id: String,
    val roomNumber: String,
    val roomType: String,
    val status: String, // EMPTY, FULL, RESERVED, MAINTENANCE
    val guestName: String = "",
    val reservationId: String? = null,
    val nights: Int = 0,
    val foodCost: String = "0.0",
    val roomCost: String = "0.0",
    val creditCardCost: String = "0.0",
    val totalCost: String = "0.0"
)

class InUseRoomsViewModel(
    private val reservationRepository: ReservationRepository,
    private val roomRepository: RoomRepository,
    private val guestRepository: GuestRepository,
    private val agencyRepository: AgencyRepository,
    private val tenantSession: TenantSession,
    private val supabaseClient: SupabaseNetworkClient
) : ViewModel() {

    private val _roomStatuses = MutableStateFlow<List<RoomStatusDisplay>>(emptyList())
    val roomStatuses: StateFlow<List<RoomStatusDisplay>> = _roomStatuses.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _availableRooms = MutableStateFlow<List<Room>>(emptyList())
    val availableRooms: StateFlow<List<Room>> = _availableRooms.asStateFlow()

    private val _agencies = MutableStateFlow<List<Agency>>(emptyList())
    val agencies: StateFlow<List<Agency>> = _agencies.asStateFlow()

    init {
        viewModelScope.launch {
            _agencies.value = agencyRepository.getAgencies()
        }
    }

    fun quickReserve(roomId: String, roomType: String, agencyId: String?, guestName: String, checkIn: String, checkOut: String) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val tenantId = tenantSession.currentTenantId ?: return@launch

                val defaultFirstName = if (agencyId != null) "Acenta" else "Bilinmeyen"
                val defaultLastName = if (agencyId != null) "Misafiri" else "Misafir"
                
                val guestFirstName = guestName.split(" ").firstOrNull()?.takeIf { it.isNotBlank() } ?: "Unknown"
                val guestLastName = guestName.split(" ").drop(1).joinToString(" ").takeIf { it.isNotBlank() } ?: "Guest"

                val guest = Guest(
                    tenantId = tenantId,
                    firstName = guestFirstName,
                    lastName = guestLastName
                )
                val createdGuest = guestRepository.createGuest(guest)
                val guestId = createdGuest?.id ?: return@launch

                val newReservation = Reservation(
                    tenantId = tenantId,
                    guestId = guestId,
                    roomId = roomId,
                    checkInDate = checkIn,
                    checkOutDate = checkOut,
                    status = "CONFIRMED",
                    roomType = roomType,
                    totalPrice = 0.0,
                    agencyId = agencyId
                )
                
                reservationRepository.createReservation(newReservation)
                loadRoomStatuses()
            } catch (e: Exception) {
                e.printStackTrace()
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun loadRoomStatuses() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val tenantId = tenantSession.currentTenantId ?: return@launch
                val rooms = roomRepository.getRooms()
                val reservations = reservationRepository.getReservations()
                
                @kotlinx.serialization.Serializable
                data class DbFolioItem(
                    val reservation_id: String,
                    val category: String,
                    val amount: Double
                )

                // Fetch all folio items for the tenant to calculate costs
                val allFolios = try {
                    supabaseClient.client.postgrest["folio_items"]
                        .select {
                            filter {
                                eq("tenant_id", tenantId)
                            }
                        }.decodeList<DbFolioItem>()
                } catch (e: Exception) {
                    emptyList()
                }
                
                val displayList = rooms.map { room ->
                    val activeRes = reservations.find { it.roomId == room.id && it.status == "CHECKED_IN" }
                    val upcomingRes = reservations.find { it.roomId == room.id && it.status == "CONFIRMED" }
                    val res = activeRes ?: upcomingRes
                    
                    val status = when {
                        room.status == "MAINTENANCE" -> "MAINTENANCE"
                        activeRes != null -> "FULL"
                        upcomingRes != null -> "RESERVED"
                        else -> "EMPTY"
                    }
                    
                    val guestName = res?.guests?.let { "${it.firstName} ${it.lastName}" } ?: ""
                    val reservationId = res?.id
                    
                    var nightsCount = 0
                    var foodCostStr = "0"
                    var roomCostStr = "0.0"
                    var creditCardCostStr = "0"
                    var totalCostStr = "0.0"
                    
                    if (res != null) {
                        try {
                            val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                            val checkInDate = LocalDate.parse(res.checkInDate)
                            nightsCount = checkInDate.daysUntil(today).coerceAtLeast(0)
                        } catch (e: Exception) {}

                        // Calculate costs from Folio
                        if (reservationId != null) {
                            val resFolios = allFolios.filter { it.reservation_id == reservationId }
                            val foodCost = resFolios.filter { it.category == "RESTAURANT" || it.category == "MINIBAR" }.sumOf { it.amount }
                            val otherCost = resFolios.filter { it.category == "OTHER" }.sumOf { it.amount }
                            
                            // Simple assumption: room cost is total price from reservation
                            val roomCost = res.totalPrice ?: 0.0
                            
                            val totalCost = roomCost + foodCost + otherCost
                            
                            foodCostStr = foodCost.toString()
                            roomCostStr = roomCost.toString()
                            totalCostStr = totalCost.toString()
                        }
                    }
                    
                    RoomStatusDisplay(
                        id = room.id ?: "",
                        roomNumber = room.roomNumber,
                        roomType = room.roomType,
                        status = status,
                        guestName = guestName,
                        reservationId = reservationId,
                        nights = nightsCount,
                        foodCost = foodCostStr,
                        roomCost = roomCostStr,
                        creditCardCost = creditCardCostStr,
                        totalCost = totalCostStr
                    )
                }
                _roomStatuses.value = displayList.sortedBy { it.roomNumber.toIntOrNull() ?: 0 }
            } catch (e: Exception) {
                e.printStackTrace()
            } finally {
                _isLoading.value = false
            }
        }
    }


    fun checkOut(reservationId: String) {
        viewModelScope.launch {
            try {
                val allRes = reservationRepository.getReservations()
                val targetRes = allRes.find { it.id == reservationId }
                if (targetRes != null) {
                    reservationRepository.checkOutAndPay(targetRes)
                    loadRoomStatuses() // Yenile
                }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    fun requestCleaning(roomId: String) {
        viewModelScope.launch {
            try {
                roomRepository.updateRoomStatus(roomId, "DIRTY")
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    fun loadAvailableRooms() {
        viewModelScope.launch {
            try {
                val allRooms = roomRepository.getRooms()
                _availableRooms.value = allRooms.filter { it.status == "AVAILABLE" || it.status == "CLEAN" }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    fun changeRoom(reservationId: String, oldRoomId: String, newRoomId: String) {
        viewModelScope.launch {
            try {
                // Rezervasyonun odasını güncelle
                reservationRepository.updateReservationStatusAndRoom(reservationId, "CHECKED_IN", newRoomId)
                // Eski odayı DIRTY yap
                roomRepository.updateRoomStatus(oldRoomId, "DIRTY")
                // Yeni odayı OCCUPIED yap
                roomRepository.updateRoomStatus(newRoomId, "OCCUPIED")
                // Eski hesapları (folio) yeni odaya aktar
                reservationRepository.transferFolioToNewRoom(reservationId, newRoomId)
                // Listeyi yenile
                loadRoomStatuses()
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }
    
    fun updateReservationDetails(reservationId: String, newGuestName: String, newCheckOutDate: String, newNotes: String) {
        viewModelScope.launch {
            try {
                val allRes = reservationRepository.getReservations()
                val targetRes = allRes.find { it.id == reservationId }
                if (targetRes != null) {
                    val updatedRes = targetRes.copy(
                        checkOutDate = newCheckOutDate,
                        specialNotes = newNotes
                    )
                    reservationRepository.updateReservation(updatedRes)
                    
                    val targetGuest = targetRes.guests
                    if (targetGuest != null) {
                        val names = newGuestName.trim().split(" ")
                        val firstName = if (names.isNotEmpty()) names.first() else ""
                        val lastName = if (names.size > 1) names.drop(1).joinToString(" ") else ""
                        val updatedGuest = targetGuest.copy(
                            firstName = firstName,
                            lastName = lastName
                        )
                        guestRepository.updateGuest(updatedGuest)
                    }
                    loadRoomStatuses()
                }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }
}

